import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface OrgMember {
	id: string;
	name: string;
}

// Members of the current user's organization, for the "Added to database by" picker
export function useOrgMembers() {
	return useQuery({
		queryKey: ['people-search-org-members'],
		queryFn: async (): Promise<OrgMember[]> => {
			const { data: { user } } = await supabase.auth.getUser();
			if (!user) throw new Error('User not authenticated');

			const { data: userOrgs, error: orgError } = await supabase
				.from('user_organization')
				.select('organization_uuid')
				.eq('user_uuid', user.id)
				.single();
			if (orgError || !userOrgs) throw orgError || new Error('User not associated with any organization');

			const { data: members, error: membersError } = await supabase
				.from('user_organization')
				.select('user_uuid')
				.eq('organization_uuid', userOrgs.organization_uuid);
			if (membersError) throw membersError;

			const userIds = (members || []).map(m => m.user_uuid);
			if (userIds.length === 0)
				return [];

			const { data: profiles, error: profilesError } = await supabase
				.from('profiles')
				.select('id, first_name, last_name, email')
				.in('id', userIds);
			if (profilesError) throw profilesError;

			return (profiles || [])
				.map(p => ({
					id: p.id,
					name: [p.first_name, p.last_name].filter(Boolean).join(' ') || p.email || p.id,
				}))
				.sort((a, b) => a.name.localeCompare(b.name));
		},
		staleTime: 5 * 60 * 1000,
	});
}
